import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Mail, MessageCircle, Smartphone } from "lucide-react";
import {
  FREQUENCIES,
  errorMessage,
  fetchPreferences,
  maskEmail,
  maskPhone,
  savePreferences,
  type Frequency,
  type PreferenceChannels,
  type PreferencesResponse,
} from "@/lib/comms";
import logoUrl from "@assets/edufocal-logo-min-50292a59_1758905778671.png";

function readToken(): string {
  try {
    return new URLSearchParams(window.location.search).get("t") || "";
  } catch {
    return "";
  }
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-chart-2 via-chart-2/90 to-primary flex items-center justify-center p-4 sm:p-6 md:p-8">
      <div className="w-full max-w-lg space-y-4">
        <div className="flex justify-center">
          <img src={logoUrl} alt="EduFocal" className="h-10 sm:h-12 w-auto" data-testid="img-preferences-logo" />
        </div>
        {children}
      </div>
    </div>
  );
}

function ChannelRow({
  id,
  label,
  hint,
  icon: Icon,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  icon: any;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border p-3">
      <div className="min-w-0 flex gap-3">
        <Icon className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
        <div className="min-w-0">
          <Label htmlFor={id} className="cursor-pointer">{label}</Label>
          <p className="text-xs text-muted-foreground leading-snug">{hint}</p>
        </div>
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onChange} data-testid={`switch-${id}`} />
    </div>
  );
}

export default function Preferences() {
  const { toast } = useToast();
  const token = useMemo(readToken, []);
  const [unsubscribeOpen, setUnsubscribeOpen] = useState(false);

  const [channels, setChannels] = useState<PreferenceChannels>({ email: true, whatsapp: true, push: true });
  const [frequency, setFrequency] = useState<Frequency>("normal");
  const [topics, setTopics] = useState<Record<string, boolean>>({});
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  const { data, isLoading, error } = useQuery<PreferencesResponse | null>({
    queryKey: ["/api/comms/preferences", token],
    queryFn: () => fetchPreferences(token),
    enabled: !!token,
    retry: false,
  });

  const hydrate = (next: PreferencesResponse) => {
    setChannels({
      email: next.channels?.email ?? true,
      whatsapp: next.channels?.whatsapp ?? true,
      push: next.channels?.push ?? true,
    });
    setFrequency(next.frequency || "normal");
    setTopics({ ...(next.topics || {}) });
    setMarketingOptIn(!!next.marketingOptIn);
  };

  useEffect(() => {
    if (data) hydrate(data);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (body: Parameters<typeof savePreferences>[1]) => savePreferences(token, body),
    onSuccess: (res, body) => {
      hydrate(res);
      const turnedEverythingOff = body.marketingOptIn === false && body.channels?.email === false;
      toast({
        title: turnedEverythingOff ? "You are unsubscribed" : "Saved",
        description: turnedEverythingOff
          ? "We will not send you anything else, other than messages about your account itself."
          : "Your choices are in place from the next message onwards.",
      });
    },
    onError: (e: unknown) => toast({ title: "Could not save that", description: errorMessage(e), variant: "destructive" }),
  });

  if (!token || data === null) {
    return (
      <Shell>
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">This link is not valid</CardTitle>
            <CardDescription>
              It may have expired, or it may have been cut short by the email programme that showed it to you. Open the newest message we sent you
              and use the link at the bottom of that one instead.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => { window.location.href = "/"; }} data-testid="button-preferences-home">
              Go to EduFocal Academy
            </Button>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  if (isLoading || !data) {
    return (
      <Shell>
        <Card>
          <CardContent className="pt-6 space-y-3">
            {error ? (
              <>
                <p className="font-medium text-destructive">We could not open your preferences</p>
                <p className="text-sm text-muted-foreground">{errorMessage(error)}</p>
              </>
            ) : (
              <>
                <Skeleton className="h-6 w-2/3" />
                <Skeleton className="h-24" />
                <Skeleton className="h-24" />
              </>
            )}
          </CardContent>
        </Card>
      </Shell>
    );
  }

  const showWhatsapp = !!data.phone;
  const showPush = data.kind === "student";
  const topicOptions = data.topicOptions || [];

  const save = () =>
    saveMutation.mutate({
      marketingOptIn,
      channels: {
        email: channels.email,
        ...(showWhatsapp ? { whatsapp: channels.whatsapp } : {}),
        ...(showPush ? { push: channels.push } : {}),
      },
      frequency,
      topics,
    });

  const unsubscribeAll = () =>
    saveMutation.mutate({
      marketingOptIn: false,
      channels: { email: false, whatsapp: false, push: false },
    });

  return (
    <Shell>
      <Card>
        <CardHeader>
          <CardTitle className="text-xl" data-testid="text-preferences-greeting">
            {data.firstName ? `Hi ${data.firstName}, you decide what we send` : "You decide what we send"}
          </CardTitle>
          <CardDescription>
            {data.email ? <>We have you as <span className="font-medium">{maskEmail(data.email)}</span></> : "We have you on file"}
            {data.phone ? <> and <span className="font-medium">{maskPhone(data.phone)}</span></> : null}. Change anything below and press save. Messages
            about the account itself, like a receipt or a password reset, always come through.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="space-y-2">
            <p className="text-sm font-medium">How we reach you</p>
            <ChannelRow
              id="pref-email"
              label="Email"
              hint="Progress, reminders and news, in your inbox."
              icon={Mail}
              checked={channels.email}
              onChange={v => setChannels(c => ({ ...c, email: v }))}
            />
            {showWhatsapp && (
              <ChannelRow
                id="pref-whatsapp"
                label="WhatsApp"
                hint="Short updates on the number we have for you."
                icon={MessageCircle}
                checked={channels.whatsapp}
                onChange={v => setChannels(c => ({ ...c, whatsapp: v }))}
              />
            )}
            {showPush && (
              <ChannelRow
                id="pref-push"
                label="Push notifications"
                hint="Nudges on the phone or tablet you practise on."
                icon={Smartphone}
                checked={channels.push}
                onChange={v => setChannels(c => ({ ...c, push: v }))}
              />
            )}
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">How often</p>
            <RadioGroup value={frequency} onValueChange={v => setFrequency(v as Frequency)} className="space-y-2">
              {FREQUENCIES.map(f => (
                <div key={f.id} className="flex items-start gap-3 rounded-md border p-3">
                  <RadioGroupItem value={f.id} id={`freq-${f.id}`} className="mt-0.5" data-testid={`radio-frequency-${f.id}`} />
                  <div className="min-w-0">
                    <Label htmlFor={`freq-${f.id}`} className="cursor-pointer">{f.label}</Label>
                    <p className="text-xs text-muted-foreground leading-snug">{f.hint}</p>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>

          {topicOptions.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">What about</p>
              <div className="space-y-2">
                {topicOptions.map(t => (
                  <div key={t.id} className="flex items-center gap-3 rounded-md border p-3">
                    <Checkbox
                      id={`topic-${t.id}`}
                      checked={topics[t.id] !== false}
                      onCheckedChange={v => setTopics(prev => ({ ...prev, [t.id]: v === true }))}
                      data-testid={`checkbox-topic-${t.id}`}
                    />
                    <Label htmlFor={`topic-${t.id}`} className="cursor-pointer font-normal">{t.label}</Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-start justify-between gap-4 rounded-md border p-3">
            <div className="min-w-0">
              <Label htmlFor="pref-marketing" className="cursor-pointer">Offers and news from EduFocal Academy</Label>
              <p className="text-xs text-muted-foreground leading-snug">
                Turn this off and you still get everything about your child's learning, just nothing promotional.
              </p>
            </div>
            <Switch id="pref-marketing" checked={marketingOptIn} onCheckedChange={setMarketingOptIn} data-testid="switch-pref-marketing" />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <Button onClick={save} disabled={saveMutation.isPending} className="sm:w-auto w-full" data-testid="button-save-preferences">
              {saveMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save my preferences"}
            </Button>
            <button
              type="button"
              className="text-sm text-muted-foreground underline hover:text-foreground text-left"
              onClick={() => setUnsubscribeOpen(true)}
              data-testid="link-unsubscribe-all"
            >
              Unsubscribe from everything
            </button>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={unsubscribeOpen} onOpenChange={setUnsubscribeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Turn everything off?</AlertDialogTitle>
            <AlertDialogDescription>
              Every channel goes off and the offers stop. That includes your child's weekly progress, so you would be finding out how they are doing
              by asking them. Messages about the account itself still come through.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep my settings</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={e => { e.preventDefault(); unsubscribeAll(); setUnsubscribeOpen(false); }}
              data-testid="button-confirm-unsubscribe-all"
            >
              {saveMutation.isPending ? "Working…" : "Unsubscribe from everything"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Shell>
  );
}
